import { Transition } from "@headlessui/react";
import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as ChevronDown } from "../../assets/ChevronDown.svg";
import { ReactComponent as SearchIcon } from "../../assets/Search.svg";
import { SkeletonBar } from "../Skeleton";
import { ReactComponent as Tick } from "../../assets/tick.svg";
import { getClients } from "../../Api";
import { updateClient } from "../../redux/filters/filters.actions";
import useDebounceState from "../../customHooks/useDebounceState";
import {
  accessorCanSelectClient,
  clientLogoFromApi,
  readRecentClients,
} from "../../utils/selectedClientStorage";

const ALL_CLIENTS_ID = "__all__";
const PANEL_WIDTH = 300;

const CLIENT_AVATAR_PALETTE = [
  { bg: "#E8EEF5", fg: "#2F4A6A" },
  { bg: "#E7F1EE", fg: "#2A5A4A" },
  { bg: "#EEE9F4", fg: "#4A3D6A" },
  { bg: "#F3EEE6", fg: "#6A4E2F" },
  { bg: "#E6F0F4", fg: "#1F5A70" },
  { bg: "#F4EBEB", fg: "#6B3A3A" },
  { bg: "#EAF0E6", fg: "#3D5A2F" },
  { bg: "#ECEEF6", fg: "#3A3F6B" },
];

const AllClientsMark = ({ size = "md", round = false }) => {
  const dim =
    size === "xs" ? "h-6 w-6" : size === "sm" ? "h-8 w-8" : "h-9 w-9";
  const icon =
    size === "xs" ? "h-3.5 w-3.5" : size === "sm" ? "h-4 w-4" : "h-[18px] w-[18px]";
  const tiles = ["#3B82F6", "#22C55E", "#A855F7", "#F59E0B"];
  return (
    <span
      className={`${dim} inline-flex items-center justify-center shrink-0 bg-white ring-1 ring-grey-100 ${
        round ? "rounded-full" : "rounded-lg"
      }`}>
      <svg
        viewBox='0 0 16 16'
        className={icon}
        aria-hidden='true'>
        <rect x='1.5' y='1.5' width='5.5' height='5.5' rx='1.2' fill={tiles[0]} />
        <rect x='9' y='1.5' width='5.5' height='5.5' rx='1.2' fill={tiles[1]} />
        <rect x='1.5' y='9' width='5.5' height='5.5' rx='1.2' fill={tiles[2]} />
        <rect x='9' y='9' width='5.5' height='5.5' rx='1.2' fill={tiles[3]} />
      </svg>
    </span>
  );
};

const initialsFromName = (name, count = 2) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, count)
    .map(word => word.charAt(0).toUpperCase())
    .join("") || "?";

const colorFromName = name => {
  const seed = String(name || "");
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CLIENT_AVATAR_PALETTE[Math.abs(hash) % CLIENT_AVATAR_PALETTE.length];
};

const clientLogoUrl = item => clientLogoFromApi(item) || null;

const LetterMark = ({ name, round = false, size = "md" }) => {
  const dim =
    size === "xs"
      ? "h-6 w-6 text-[9px]"
      : size === "sm"
      ? "h-8 w-8 text-[11px]"
      : "h-9 w-9 text-[12px]";
  const palette = colorFromName(name);
  return (
    <span
      title={name}
      style={{ backgroundColor: palette.bg, color: palette.fg }}
      className={`${dim} inline-flex items-center justify-center shrink-0 font-semibold tracking-wide ${
        round ? "rounded-full" : "rounded-lg"
      }`}>
      {initialsFromName(name, 2)}
    </span>
  );
};

const ClientAvatar = ({ name, logo, round = false, size = "md" }) => {
  const [failed, setFailed] = useState(false);
  const dim =
    size === "xs" ? "h-6 w-6" : size === "sm" ? "h-8 w-8" : "h-9 w-9";

  useEffect(() => {
    setFailed(false);
  }, [logo, name]);

  if (!name && !logo) {
    return <AllClientsMark size={size} round={round} />;
  }

  if (!logo || failed) {
    return <LetterMark name={name} round={round} size={size} />;
  }

  return (
    <img
      key={logo}
      src={logo}
      alt=''
      className={`${dim} object-contain bg-white shrink-0 p-0.5 ring-1 ring-grey-100 ${
        round ? "rounded-full" : "rounded-lg"
      }`}
      onError={() => setFailed(true)}
    />
  );
};

const ShortcutHint = () => {
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  return (
    <span className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center rounded-md border border-grey-100 bg-white px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-grey-500'>
      {isMac ? "⌘ K" : "Ctrl K"}
    </span>
  );
};

const SectionLabel = ({ children }) => (
  <div className='px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-grey-500'>
    {children}
  </div>
);

const ClientRow = ({
  name,
  logo,
  isAll = false,
  selected,
  active,
  round,
  onClick,
  onMouseEnter,
}) => (
  <button
    type='button'
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    className={`flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors ${
      selected
        ? "bg-[#F3F8FF]"
        : active
        ? "bg-grey-50"
        : "bg-transparent hover:bg-grey-50"
    }`}>
    <ClientAvatar
      name={isAll ? "" : name}
      logo={isAll ? null : logo}
      round={round}
      size='sm'
    />
    <span className='min-w-0 flex-1'>
      <span className='block truncate text-[13px] font-semibold text-grey-900'>
        {name}
      </span>
    </span>
    {selected && (
      <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-main text-white'>
        <Tick className='h-3 w-3 fill-current' />
      </span>
    )}
  </button>
);

const ClientRowSkeleton = () => (
  <div className='flex w-full items-center gap-2.5 rounded-xl px-2 py-2'>
    <span className='block h-7 w-7 shrink-0 animate-pulse rounded-full bg-grey-100' />
    <SkeletonBar width='60%' height={13} />
  </div>
);

const toOption = item => ({
  value: item.uid,
  label: item.name,
  logo: clientLogoUrl(item) || undefined,
});

export const OPEN_CLIENT_SELECTOR_EVENT = "hackle:open-client-selector";

export function openClientSelector(options = {}) {
  window.dispatchEvent(
    new CustomEvent(OPEN_CLIENT_SELECTOR_EVENT, { detail: options }),
  );
}

export default function ClientSelector({
  id,
  isDisabled,
  compact = false,
  variant = "default",
  className = "",
}) {
  const isHeader = variant === "header";
  const { client } = useSelector(state => state.filters);
  const { accessor } = useSelector(state => state.accessor);
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [clientList, setClientList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelPos, setPanelPos] = useState(null);
  const [recents, setRecents] = useState([]);

  const isOpenRef = useRef(false);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  const searchRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const isDisabledRef = useRef(isDisabled);

  const canSelectClient = accessorCanSelectClient(accessor);
  const userUid = accessor?.uid || null;

  const debouncedSearch = useDebounceState({
    value: search,
    delay: 400,
  });

  useEffect(() => {
    if (!userUid || search.trim()) {
      setRecents([]);
      return;
    }
    const stored = readRecentClients(userUid);
    const byId = new Map(clientList.map(item => [item.uid, item]));
    setRecents(
      stored.map(item => {
        const live = byId.get(item.value);
        return live ? toOption(live) : item;
      }),
    );
  }, [userUid, search, clientList, isOpen]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = [...clientList].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
    if (!q) return rows;
    return rows.filter(item => (item.name || "").toLowerCase().includes(q));
  }, [clientList, search]);

  const showAllWorkspace = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q || "all clients".startsWith(q);
  }, [search]);

  const flatRows = useMemo(() => {
    const rows = [];
    recents.forEach(item =>
      rows.push({ key: `recent-${item.value}`, kind: "client", item }),
    );
    if (showAllWorkspace) {
      rows.push({ key: ALL_CLIENTS_ID, kind: "all" });
    }
    filteredClients.forEach(item =>
      rows.push({ key: `all-${item.uid}`, kind: "client", item: toOption(item) }),
    );
    return rows;
  }, [recents, showAllWorkspace, filteredClients]);

  useEffect(() => {
    isDisabledRef.current = isDisabled;
  }, [isDisabled]);

  useEffect(() => {
    if (!isOpen || !canSelectClient) return undefined;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const { clients } = await getClients({
          search: debouncedSearch,
          pageSize: 100,
        });
        if (!cancelled) setClientList(clients || []);
      } catch (error) {
        console.log(error);
        if (!cancelled) setClientList([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedSearch, canSelectClient]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const selectedIdx = flatRows.findIndex(row =>
      row.kind === "all" ? !client : row.item.value === client?.value,
    );
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
  }, [isOpen, search, flatRows, client]);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPanelPos(null);
      return undefined;
    }
    const place = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const left = compact
        ? Math.min(rect.right + 8, window.innerWidth - PANEL_WIDTH - 8)
        : Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8);
      const below = rect.bottom + 8;
      const height = Math.min(460, window.innerHeight - 24);
      const top =
        below + height > window.innerHeight - 8
          ? Math.max(8, rect.top - height - 8)
          : below;
      setPanelPos({
        top,
        left: Math.max(8, left),
        width: PANEL_WIDTH,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isOpen, compact]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = event => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen]);

  const closePanel = () => {
    setIsOpen(false);
    setSearch("");
  };

  const openPanel = () => {
    if (isDisabled) return;
    setSearch("");
    setIsOpen(true);
  };

  useEffect(() => {
    if (!canSelectClient) return undefined;
    const onRequestOpen = () => {
      if (isDisabledRef.current) return;
      setSearch("");
      setIsOpen(true);
    };
    const onShortcut = event => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") {
        return;
      }
      if (event.repeat) return;
      event.preventDefault();
      if (isDisabledRef.current) return;
      if (isOpenRef.current) {
        searchRef.current?.focus();
        return;
      }
      setSearch("");
      setIsOpen(true);
    };
    window.addEventListener(OPEN_CLIENT_SELECTOR_EVENT, onRequestOpen);
    window.addEventListener("keydown", onShortcut);
    return () => {
      window.removeEventListener(OPEN_CLIENT_SELECTOR_EVENT, onRequestOpen);
      window.removeEventListener("keydown", onShortcut);
    };
  }, [canSelectClient]);

  const selectClient = next => {
    dispatch(updateClient(next));
    setIsOpen(false);
    setSearch("");
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePanel();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex(i => Math.min(Math.max(flatRows.length - 1, 0), i + 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex(i => Math.max(0, i - 1));
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const row = flatRows[activeIndex];
        if (!row) return;
        if (row.kind === "all") selectClient(null);
        else selectClient(row.item);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, flatRows, activeIndex]);

  if (!canSelectClient) return null;

  const selectedLabel = client?.label || "All clients";
  let rowCursor = -1;
  const nextIndex = () => {
    rowCursor += 1;
    return rowCursor;
  };

  const avatarSize = isHeader ? "sm" : compact ? "md" : "sm";

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        id={id || "client-selector"}
        disabled={isDisabled}
        onClick={() => (isOpen ? closePanel() : openPanel())}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-label={`Client: ${selectedLabel}`}
        className={`flex items-center min-w-0 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-lightest disabled:opacity-50 disabled:cursor-not-allowed ${
          compact
            ? "justify-center h-9 w-full rounded-lg hover:bg-grey-50"
            : isHeader
            ? `h-11 w-full gap-2.5 rounded-xl border px-3 ${className} ${
                isOpen
                  ? "border-primary-main bg-white ring-2 ring-primary-lightest"
                  : "border-grey-200 bg-white hover:border-grey-300"
              }`
            : `h-auto w-full gap-2.5 rounded-xl border px-2.5 py-2 ${className} ${
                isOpen
                  ? "border-primary-main bg-[#F3F8FF]"
                  : "border-grey-100 bg-white hover:border-primary-light"
              }`
        }`}>
        {client ? (
          <ClientAvatar
            name={client.label}
            logo={client.logo}
            round
            size={avatarSize}
          />
        ) : (
          <ClientAvatar name='' logo={null} round size={avatarSize} />
        )}
        {!compact && (
          <>
            <span className='min-w-0 flex-1'>
              <span
                className={`block truncate text-grey-900 ${
                  isHeader
                    ? "text-sm font-medium"
                    : "text-[13px] font-semibold"
                }`}>
                {selectedLabel}
              </span>
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 shrink-0 fill-current text-grey-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </>
        )}
      </button>

      {createPortal(
        <Transition
          show={isOpen && Boolean(panelPos)}
          as={Fragment}
          enter='ease-out duration-150'
          enterFrom='opacity-0 translate-y-1'
          enterTo='opacity-100 translate-y-0'
          leave='ease-in duration-100'
          leaveFrom='opacity-100 translate-y-0'
          leaveTo='opacity-0 translate-y-1'>
          <div
            ref={panelRef}
            role='listbox'
            aria-label='Select client'
            className='fixed z-[70] rounded-2xl border border-grey-100 bg-white p-2 shadow-toast-box'
            style={panelPos || { display: "none" }}>
            <div className='relative px-1 pt-1 pb-1'>
              <SearchIcon className='pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 fill-current text-grey-500' />
              <input
                ref={searchRef}
                type='text'
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder='Search clients...'
                className='w-full h-10 rounded-xl border border-grey-100 bg-grey-50 pl-9 pr-14 text-sm text-grey-900 placeholder:text-grey-500 focus:outline-none focus:border-primary-main focus:bg-white focus:ring-0'
              />
              <ShortcutHint />
            </div>

            <div className='mt-1 max-h-[22rem] overflow-y-auto px-0.5 pb-1'>
              {isLoading ? (
                <div className='px-0.5'>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <ClientRowSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <>
                  {recents.length > 0 && (
                    <>
                      <SectionLabel>Recent clients</SectionLabel>
                      {recents.map(item => {
                        const index = nextIndex();
                        return (
                          <ClientRow
                            key={`recent-${item.value}`}
                            name={item.label}
                            logo={item.logo}
                            round
                            selected={client?.value === item.value}
                            active={activeIndex === index}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => selectClient(item)}
                          />
                        );
                      })}
                    </>
                  )}

                  <SectionLabel>All clients</SectionLabel>
                  {showAllWorkspace &&
                    (() => {
                      const index = nextIndex();
                      return (
                        <ClientRow
                          key={ALL_CLIENTS_ID}
                          name='All clients'
                          logo={null}
                          isAll
                          round
                          selected={!client}
                          active={activeIndex === index}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => selectClient(null)}
                        />
                      );
                    })()}
                  {filteredClients.length === 0 && !showAllWorkspace ? (
                    <div className='px-2 py-6 text-center text-sm text-grey-500'>
                      No clients match.
                    </div>
                  ) : (
                    filteredClients.map(item => {
                      const index = nextIndex();
                      const option = toOption(item);
                      return (
                        <ClientRow
                          key={item.uid}
                          name={item.name}
                          logo={option.logo}
                          selected={client?.value === item.uid}
                          active={activeIndex === index}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => selectClient(option)}
                        />
                      );
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </Transition>,
        document.body,
      )}
    </>
  );
}
